import { Controller } from "@hotwired/stimulus"

interface CalNamespaceApi {
  (...args: unknown[]): void
  q: unknown[]
}

interface CalGlobal {
  (...args: unknown[]): void
  loaded?: boolean
  ns?: Record<string, CalNamespaceApi>
  q?: unknown[]
  config?: { forwardQueryParams?: boolean }
}

declare global {
  interface Window {
    Cal?: CalGlobal
  }
}

// Official Cal.com embed loader, ported (typed) from @calcom/embed-snippet
// (packages/embeds/embed-snippet/src/index.ts). Defines a queueing
// `window.Cal` and injects embed.js once; calls made before the script
// arrives are queued and replayed by it on load.
function ensureCalGlobal(embedJsUrl: string): CalGlobal {
  if (window.Cal) return window.Cal

  const push = (api: { q: unknown[] }, args: unknown) => {
    api.q.push(args)
  }

  const cal: CalGlobal = function (...args: unknown[]) {
    if (!cal.loaded) {
      cal.ns = {}
      cal.q = cal.q ?? []
      document.head.appendChild(document.createElement("script")).src =
        embedJsUrl
      cal.loaded = true
    }

    if (args[0] === "init" && typeof args[1] === "string") {
      const namespace = args[1]
      const api = function (...apiArgs: unknown[]) {
        push(api, apiArgs)
      } as CalNamespaceApi
      api.q = api.q ?? []
      // Never overridden on re-execution — same guarantee as the snippet.
      cal.ns![namespace] = cal.ns![namespace] ?? api
      push(cal.ns![namespace], args)
      push(cal as CalNamespaceApi, ["initNamespace", namespace])
      return
    }

    push(cal as CalNamespaceApi, args)
  }

  window.Cal = cal
  return cal
}

// Connects to data-controller="cal-embed"
// Mounts a Cal.com inline booking embed into the mount target. Named by
// behaviour, not feature: any page can embed any Cal.com event type by
// passing its own namespace/cal-link values.
export default class extends Controller {
  static targets = ["mount"]
  static values = {
    namespace: String,
    calLink: String,
    origin: { type: String, default: "https://app.cal.com" },
    forwardQueryParams: { type: Boolean, default: true },
  }

  declare readonly mountTarget: HTMLElement
  declare readonly namespaceValue: string
  declare readonly calLinkValue: string
  declare readonly originValue: string
  declare readonly forwardQueryParamsValue: boolean

  // Held from connect(): by the time disconnect() runs, target lookup no
  // longer resolves (Stimulus has already torn the scope down), so the
  // element has to be captured while it is still reachable.
  private mountElement: HTMLElement | null = null

  // Bound once per connect() so disconnect() can remove the exact same
  // reference — a fresh .bind() in disconnect would silently remove nothing.
  private boundTeardown: (() => void) | null = null

  connect() {
    const cal = ensureCalGlobal(`${this.originValue}/embed/embed.js`)

    cal("init", this.namespaceValue, { origin: this.originValue })

    // Forward the page's query params (?ref=...) into the Cal.com booking
    // record natively — upstream defaults this off, so opt in explicitly.
    // This is a PAGE-GLOBAL switch, not a per-embed one, and `window.Cal`
    // outlives Turbo visits: the last embed to connect wins for every booker
    // on the page and for the rest of the session. So every cal-embed on the
    // site must pass the same value, or attribution silently drops for the
    // ones that disagree.
    cal.config = cal.config ?? {}
    cal.config.forwardQueryParams = this.forwardQueryParamsValue

    // Re-connects (Turbo restore visits) get a clean mount, not a second
    // booker iframe from the cached copy.
    this.mountElement = this.mountTarget
    this.mountElement.replaceChildren()

    // Registered here, not as a data-action in the view: the before-cache
    // teardown is intrinsic to the embed, so the controller owns its own
    // lifecycle — a DOM-wired action would make every call site responsible
    // for remembering it (review judgment on #1639).
    this.boundTeardown = this.teardown.bind(this)
    document.addEventListener("turbo:before-cache", this.boundTeardown)

    cal.ns?.[this.namespaceValue]?.("inline", {
      elementOrSelector: this.mountTarget,
      calLink: this.calLinkValue,
      config: { layout: "month_view" },
    })
  }

  // Leave nothing for Turbo to snapshot: caching the page with a live
  // app.cal.com iframe in it means a restore visit inserts the clone into the
  // document, firing a second full booker load from the third party before
  // connect() throws it away.
  //
  // Listening on turbo:before-cache (not relying on disconnect() alone)
  // because disconnect is a race: Turbo clones the body one event-loop tick
  // after firing before-cache, while the body swap that disconnects Stimulus
  // controllers runs on the microtask chain — so the snapshot can be taken
  // with the iframe still in it. before-cache is the hook that is ordered
  // ahead of the clone.
  private teardown() {
    this.mountElement?.replaceChildren()
  }

  disconnect() {
    if (this.boundTeardown) {
      document.removeEventListener("turbo:before-cache", this.boundTeardown)
      this.boundTeardown = null
    }
    this.teardown()
    this.mountElement = null
  }
}
